/**
 * ThemeContext.jsx - Granullo Glass System v2
 * 
 * REQUIREMENTS:
 * - Support Dark, Light, and System modes with manual override
 * - Persist preference in localStorage (immediate) + database (for logged-in users)
 * - Apply data-theme attribute on <html> for CSS token switching
 * - Respect prefers-reduced-motion for transitions
 * - Prevent FOUC (initial theme applied by inline script in index.html)
 * 
 * DEFAULT: System (prefers-color-scheme), fallback to Dark if undetectable
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const THEME_KEY = 'granullo_theme';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ThemeContext = createContext({
  theme: 'system',
  resolvedTheme: 'dark',
  setTheme: () => {},
});

/**
 * Get system color scheme preference
 */
function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'light'; // Fallback to light — editorial trust OS species
}

/**
 * Apply theme to document element
 * Sets data-theme attribute and class for CSS targeting
 */
function applyThemeToDocument(themeName) {
  const root = document.documentElement;
  const resolved = themeName === 'system' ? getSystemTheme() : themeName;
  
  // Set data-theme attribute (primary CSS hook)
  root.setAttribute('data-theme', resolved);
  
  // Set class for Tailwind compatibility
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
  
  // Set color-scheme for native form elements
  root.style.colorScheme = resolved;
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', resolved === 'dark' ? '#0c1018' : '#f8f9fc');
  }
  
  return resolved;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState('system');
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    // Initialize from document (already set by inline script)
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  });

  /**
   * Sync theme preference to database (for logged-in users)
   * Non-blocking - fire and forget
   */
  const syncToDatabase = useCallback(async (themePref) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return; // Not logged in
      
      await fetch(`${API_URL}/api/profiles/me/preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ theme_preference: themePref }),
      });
    } catch (e) {
      // Silent fail - localStorage is primary, DB is secondary
      console.debug('Theme sync to DB failed:', e);
    }
  }, []);

  /**
   * Set theme and persist
   */
  const setTheme = useCallback((newTheme) => {
    // Validate theme value
    if (!['system', 'light', 'dark'].includes(newTheme)) {
      newTheme = 'system';
    }
    
    setThemeState(newTheme);
    
    // Persist to localStorage
    try {
      localStorage.setItem(THEME_KEY, newTheme);
    } catch (e) {
      // localStorage not available
    }
    
    // Apply to document
    const resolved = applyThemeToDocument(newTheme);
    setResolvedTheme(resolved);
    
    // Sync to database (non-blocking)
    syncToDatabase(newTheme);
  }, [syncToDatabase]);

  /**
   * Initialize theme on mount
   * Note: The inline script in index.html handles FOUC prevention
   * This just syncs React state with what's already applied
   */
  useEffect(() => {
    let savedTheme = 'system';
    
    try {
      savedTheme = localStorage.getItem(THEME_KEY) || 'system';
    } catch (e) {
      // localStorage not available
    }
    
    // Validate saved theme
    if (!['system', 'light', 'dark'].includes(savedTheme)) {
      savedTheme = 'system';
    }
    
    setThemeState(savedTheme);
    const resolved = applyThemeToDocument(savedTheme);
    setResolvedTheme(resolved);
  }, []);

  /**
   * Listen for system preference changes when theme is 'system'
   */
  useEffect(() => {
    if (theme !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      const resolved = applyThemeToDocument('system');
      setResolvedTheme(resolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
